<template>
	<div
		id="hero"
		class="container-expanded container"
		:class="{
			'remove-text-shadow': resource.remove_heading_text_shadow,
			'left-align-heading': resource.left_align_heading,
			'short-heading': resource.post_title.length <= 10,
			'has-image': hasHeroImage,
		}"
	>
		<div
			v-if="resource.post_title"
			class="title"
		>
			<h1>
				{{ resource.post_title }}
			</h1>
		</div>

		<div
			v-if="shouldShowAudienceText"
			class="audience"
		>
			{{ audienceDisplayText }}
		</div>
	</div>
</template>

<script lang="ts">
import { type PropType, defineComponent } from 'vue';
import type { Resource } from '@/interfaces/resourceInterface';
import { capitalise, wrapBackgroundImageUrlToCss } from '@/helpers';
import { getAudienceFromKey, resourceTypeKeys } from '@/interfaces/resourceFilterInterface';

export default defineComponent({
	props: {
		resource: {
			type: Object as PropType<Resource>,
			required: true,
		},
	},
	computed: {
		shouldShowAudienceText() {
			return this.resource.type === resourceTypeKeys.Article;
		},
		audienceDisplayText() {
			if (!this.shouldShowAudienceText) {
				return false;
			}

			const audience = capitalise(this.resource.audience);

			return `${audience} practice article`;
		},
		audienceColor() {
			return getAudienceFromKey(this.resource.audience).color;
		},
		hasHeroImage() {
			return !this.resource.hero.default;
		},
		herourl() {
			return wrapBackgroundImageUrlToCss(this.resource.hero.src);
		},
		heroretinaurl() {
			return wrapBackgroundImageUrlToCss(this.resource.hero.retina);
		},
		heroposition() {
			return this.resource.hero.left ? `${this.resource?.hero.left}% ${this.resource?.hero.top}%` : 'center';
		},
	},
});
</script>

<style scoped lang="scss">
@use '@/styles/utils';

#hero {
	display: flex;
	flex-direction: column-reverse;
	justify-content: space-between;
	/* stylelint-disable-next-line declaration-property-value-no-unknown */
	background-image: v-bind(herourl);
	/* stylelint-disable-next-line declaration-property-value-no-unknown */
	background-position: v-bind(heroposition);
	background-size: cover;
	background-repeat: no-repeat;
	background-color: rgb(var(--rs-color-brand-honey) / 70%);
	padding-bottom: 25px;
	min-height: 250px;

	.audience {
		--audience-color: v-bind('audienceColor');

		display: flex;
		align-items: center;
		background-color: rgb(var(--audience-color));
		padding: 0 var(--rs-layout-content-padding);
		width: 100%;
		min-height: 32px;
		color: white;
		font-size: 1.6rem;
		letter-spacing: 1px;
		text-transform: uppercase;

		@media (width >= 640px) {
			max-width: 348px;
		}
	}

	.title {
		display: flex;
		justify-content: flex-end;
		align-items: center;

		h1 {
			// Don't use text shadow as its not well support by PDFs
			filter: drop-shadow(1px 1px 4px black);
			color: white;
			font-size: 3.6rem;
			font-family: var(--rs-typography-font-heading);
		}
	}

	&.remove-text-shadow {
		h1 {
			text-shadow: none;
		}
	}

	&.left-align-heading {
		align-items: flex-start;

		h1 {
			text-align: left;
		}
	}

	&.short-heading {
		h1 {
			@media screen and (width >= 1024px) {
				margin-right: calc(100vw - 1024px);
			}

			@media screen and (width >= 1200px) {
				margin-right: 180px;
			}
		}
	}
}

@media (width >= 1024px), print {
	#hero {
		/* stylelint-disable-next-line declaration-property-value-no-unknown */
		background-image: v-bind(heroretinaurl);
		padding-bottom: 50px;
		text-align: right;

		.title {
			h1 {
				font-size: 5rem;
			}
		}
	}
}

@media only screen and (width >= 1024px) {
	#hero {
		&.has-image {
			min-height: 30vw;
		}
	}
}

@media only print {
	#hero {
		padding-right: 30px !important;
		padding-bottom: 30px;
		min-height: 180px;

		.title {
			h1 {
				font-size: calc(4rem * var(--rs-scale-factor-small));
			}
		}

		.audience {
			padding: 15px 10px 5px 10px;
			width: var(--rs-print-sidebar-width);
		}
	}
}
</style>
